/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import AlertModal from 'shared/components/AlertModal';
import MainSpinner from 'shared/components/MainSpinner';
import useApi from 'hooks/useApi';
import { StateFormWrapper } from './styles';
import Form from './Form';

const StateForm = ({ onCancel, currentState, handleRefresh, urlFragment }) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const auth = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [showError, setShowError] = useState(false);
  const [isClientState, setClientState] = useState(false);
  const [isDocumentState, setDocumentState] = useState(false);
  const [{ loading: loadingCreate }, createState] = customAreaApi(
    {
      url: `${window.API_V1}/${urlFragment}/${auth.cusNavId}`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingUpdate }, updateState] = customAreaApi(
    {
      url: `${window.API_V1}/${urlFragment}/${currentState?.clientNavisionId}/modify`,
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onCreateState = async (values) => {
    try {
      const newData = [
        {
          color: values.stateColor,
          I18List: [
            {
              I18Code: selectedDefaultLanguage,
              I18Name: values.stateName,
            },
          ],
        },
      ];
      const {
        data: { dataErrors },
      } = await createState({ data: newData });
      handleRefresh();
      onCancel();
      if (dataErrors.length) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setShowError(true);
    }
  };

  const onEditState = async (values) => {
    try {
      const oldI18List = currentState.i18List.filter((x) => selectedDefaultLanguage !== x.i18Code);
      const newData = [
        {
          id: currentState?.id,
          color: values.stateColor,
          i18List: [
            ...oldI18List,
            {
              i18Code: selectedDefaultLanguage,
              i18Name: values.stateName,
            },
          ],
        },
      ];

      const {
        data: { dataErrors },
      } = await updateState({ data: newData });
      handleRefresh();
      onCancel();
      if (dataErrors.length) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setShowError(true);
    }
  };

  const onAddState = (values) => {
    if (currentState) {
      onEditState(values);
    } else {
      onCreateState(values);
    }
  };

  useEffect(() => {
    setClientState(urlFragment === 'cus/statesClients');
    setDocumentState(urlFragment === 'cus/states');
  }, [urlFragment]);

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>
            {currentState && isDocumentState
              ? t('state_title_edit')
              : currentState && isClientState
              ? t('client_state_title_edit')
              : isDocumentState
              ? t('state_title_new')
              : t('client_state_title_new')}
          </h3>
        </Col>
      </Row>
    );
  };
  return (
    <StateFormWrapper>
      <Title />
      <Form onCancel={onCancel} onSubmit={onAddState} defaultValue={currentState} />
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={t('custom_field_operation_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      {(loadingCreate || loadingUpdate) && <MainSpinner />}
    </StateFormWrapper>
  );
};

StateForm.defaultProps = {
  currentState: null,
};

StateForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  urlFragment: PropTypes.string.isRequired,
  currentState: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    color: PropTypes.string,
    clientNavisionId: PropTypes.string,
    i18List: PropTypes.arrayOf(
      PropTypes.shape({
        i18Code: PropTypes.string,
      })
    ),
  }),
  handleRefresh: PropTypes.func.isRequired,
};

export default StateForm;
